import { useEffect, useRef, useState } from 'react'

import { shipmentStatuses, useFetch } from '@reveni-io/reveni-ui'

import { lineItemStatuses } from '@/parcels/constants'
import { isInboundParcel, isTrackingParcel } from '@/parcels/helpers/helpers'
import { discardedItemsActionsValues } from '@/parcels/pages/ParcelPreparePage/SummaryCard/DiscardedItemsActions/constants'
import parcelServices from '@/parcels/services/services'
import routes from '@/Router/routes.constants'

const usePrepareParcelItems = ({
  items,
  navigation,
  setToastInfo,
  storeId,
  parcelId,
  relatedId,
  fetchParcel,
  updateContext,
  carrierServiceId,
  returnToParcelsPageAfterPrepare,
  senderLocationId,
  locations,
  parcel,
  packaging,
}) => {
  const { fetchData: prepareParcelRequest, loading: preparingParcel } = useFetch(parcelServices.prepareParcel, {
    doInitialRequest: false,
  })
  const [itemsToPrepare, setItemsToPrepare] = useState(null)
  const [productCodeError, setProductCodeError] = useState(null)
  const [productCode, setProductCode] = useState('')
  const [notes, setNotes] = useState('')
  const [discardAction, setDiscardAction] = useState(discardedItemsActionsValues.discard)
  const [carrierServiceForNewShipment, setCarrierServiceForNewShipment] = useState(null)
  const [locationForNewShipment, setLocationForNewShipment] = useState(null)
  const [selectedPackaging, setSelectedPackaging] = useState(null)
  const parcelIdRef = useRef(parcelId)

  useEffect(() => {
    parcelIdRef.current = parcelId
  }, [parcelId])

  useEffect(() => {
    if (items?.length) {
      setItemsToPrepare(
        items.map(item => ({
          ...item,
          newStatus: [lineItemStatuses.packed, lineItemStatuses.discarded].includes(item.status) ? item.status : null,
        }))
      )
    }
  }, [items, parcelId])

  useEffect(() => {
    if (carrierServiceId) {
      setCarrierServiceForNewShipment(carrierServiceId)
    }
  }, [carrierServiceId])

  useEffect(() => {
    if (locations?.length && senderLocationId) {
      const location = locations.find(location => location.id === senderLocationId)
      setLocationForNewShipment(location?.id || null)
    }
  }, [locations, senderLocationId])

  useEffect(() => {
    if (packaging?.length) {
      const senderLocation = locations?.find(location => location.id === senderLocationId)
      if (senderLocation?.default_packaging_id) {
        setSelectedPackaging(senderLocation.default_packaging_id)
      } else {
        const defaultPackaging = packaging.find(p => p.is_default)
        setSelectedPackaging(defaultPackaging?.id || null)
      }
    }
  }, [packaging, locations, senderLocationId])

  const changeItemStatus = (lineItemId, status) => {
    const updatedLineItems = itemsToPrepare.map(item => {
      if (item.id === lineItemId) {
        return { ...item, newStatus: status }
      }
      return item
    })

    setItemsToPrepare(updatedLineItems)
  }

  const getPendingItemBySkuOrBarcode = code => {
    const parsedCode = String(code).trim()

    const item = itemsToPrepare.find(
      item =>
        (String(item.sku) === parsedCode ||
          String(item?.variant?.sku) === parsedCode ||
          String(item.barcode) === parsedCode) &&
        item.status === lineItemStatuses.pending &&
        !item.newStatus
    )

    return item
  }

  const onChangeProductCode = productCode => {
    setProductCodeError(null)
    setProductCode(productCode)
    if (productCode) {
      const pendingItem = getPendingItemBySkuOrBarcode(productCode)

      if (pendingItem) {
        changeItemStatus(pendingItem.id, lineItemStatuses.packed)
        setProductCode('')
      } else {
        setProductCodeError('El producto buscado no se encuentra en el pedido.')
      }
    }
  }

  const handleChangeNotes = e => {
    setNotes(e.target.value)
  }

  const isParcelEnableToPrepare = () => {
    if (!itemsToPrepare?.every(item => item.newStatus)) return false

    if (itemsToPrepare?.every(item => item.newStatus === lineItemStatuses.discarded)) return false

    if (hasItemsToDiscard() && discardAction === discardedItemsActionsValues.createNewShipment) {
      if (!carrierServiceForNewShipment) return false
      if (!locationForNewShipment) return false
    }

    return true
  }

  const hasItemsToDiscard = () => {
    return itemsToPrepare?.some(item => item.newStatus === lineItemStatuses.discarded)
  }

  const prepareParcel = async (defaultLineItemStatus, doRedirect = true) => {
    const payload = {
      notes,
      line_items: itemsToPrepare.map(item => ({ id: item.id, status: item.newStatus || defaultLineItemStatus })),
      packaging_id: selectedPackaging,
    }

    if (hasItemsToDiscard()) {
      payload.discard_action = discardAction

      if (discardAction === discardedItemsActionsValues.createNewShipment) {
        payload.new_shipment_carrier_service_id = carrierServiceForNewShipment
        payload.new_shipment_location_id = locationForNewShipment
      }
    }

    try {
      const response = await prepareParcelRequest({
        storeId,
        relatedId,
        payload,
      })

      if (response?.labels?.length) {
        window.open(response.labels[0].label_url, '_blank')
      }

      if (returnToParcelsPageAfterPrepare) {
        navigation(routes.parcelsPage.index)
      } else {
        if (doRedirect) navigation(routes.parcelDetailPage.replace(':id', parcelId))
      }
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const onPrepareParcel = () => {
    if (updateContext) {
      updateContext(context => ({
        ...context,
        openParcelDetailDrawerWithId: parcelId,
        returnToParcelsPageAfterPrepare: true,
      }))
    }
    navigation(routes.parcelPreparePage.replace(':id', parcelId))
  }

  const onAutoPrepareParcel = async () => {
    const preparedParcelId = parcelId

    await prepareParcel(lineItemStatuses.packed, false)

    if (parcelIdRef.current === preparedParcelId) await fetchParcel()
  }

  const allowPrepareParcel =
    parcel?.status === shipmentStatuses.requested && !isInboundParcel(parcel) && !isTrackingParcel(parcel)

  const allowAmendPackingStatus =
    parcel?.status === shipmentStatuses.ready &&
    items?.some(item => item.status === lineItemStatuses.discarded) &&
    !isInboundParcel(parcel) &&
    !isTrackingParcel(parcel)

  return {
    itemsToPrepare,
    changeItemStatus,
    onChangeProductCode,
    getPendingItemBySkuOrBarcode,
    productCodeError,
    productCode,
    setProductCode,
    notes,
    handleChangeNotes,
    isParcelEnableToPrepare,
    prepareParcel,
    preparingParcel,
    onPrepareParcel,
    onAutoPrepareParcel,
    setDiscardAction,
    discardAction,
    carrierServiceForNewShipment,
    setCarrierServiceForNewShipment,
    locationForNewShipment,
    setLocationForNewShipment,
    hasItemsToDiscard,
    allowPrepareParcel,
    allowAmendPackingStatus,
    selectedPackaging,
    setSelectedPackaging,
  }
}

export default usePrepareParcelItems
